// Enlaces gratuitos de Google Maps URLs (sin clave de API).
// Límites oficiales: 3 paradas intermedias en navegadores móviles, 9 en el resto; URL máx. 2048 caracteres.
export const MAX_URL_LENGTH = 2048;

// iPadOS se presenta con UA de escritorio: se detecta por pantalla táctil.
export const isMobileDevice = () => typeof navigator !== 'undefined' && (
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
  || (/Macintosh/.test(navigator.userAgent || '') && navigator.maxTouchPoints > 1)
);

export const waypointLimit = () => (isMobileDevice() ? 3 : 9);

const coordParam = (lat, lng) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;

// Punto de salida/regreso → parámetro de Google Maps. «actual» se omite para que Maps use la ubicación del dispositivo.
export const puntoParam = (p) => {
  if (!p) return null;
  if (p.tipo === 'texto') return p.texto || null;
  if (p.tipo === 'punto' && p.lat != null && p.lng != null) return coordParam(p.lat, p.lng);
  return null;
};

export const puntoLabel = (p) => {
  if (!p) return '—';
  if (p.tipo === 'texto') return p.texto;
  if (p.tipo === 'punto') return p.texto || `Punto ${Number(p.lat).toFixed(5)}, ${Number(p.lng).toFixed(5)}`;
  return 'Ubicación actual del dispositivo';
};

export const directionsUrl = (origin, destination, waypoints = []) => {
  const params = new URLSearchParams();
  params.set('api', '1');
  if (origin) params.set('origin', origin);
  params.set('destination', destination);
  if (waypoints.length) params.set('waypoints', waypoints.join('|'));
  params.set('travelmode', 'driving');
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

// Destino único: sin origen para que Google Maps parta de la ubicación actual del mediador.
export const visitaUrl = (v) => directionsUrl(null, coordParam(v.lat, v.lng));

/**
 * Divide el recorrido (salida → visitas en orden → regreso) en etapas que respetan
 * el límite de paradas y la longitud del enlace. Nunca omite paradas: cada etapa
 * empieza donde terminó la anterior.
 */
export const buildEtapas = (rec, limit = waypointLimit()) => {
  const visitas = (rec.visitas || []).filter(v => v.disponible !== false && v.lat != null && v.lng != null);
  const stops = visitas.map((v, i) => ({ param: coordParam(v.lat, v.lng), label: `Visita ${i + 1}${v.codigo_npl ? ` (${v.codigo_npl})` : ''}` }));
  const regreso = puntoParam(rec.regreso);
  if (regreso) stops.push({ param: regreso, label: 'Regreso' });
  const etapas = [];
  let origin = puntoParam(rec.salida);
  let originLabel = 'Salida';
  let idx = 0;
  while (idx < stops.length) {
    let size = Math.min(limit + 1, stops.length - idx);
    let url;
    for (;;) {
      const chunk = stops.slice(idx, idx + size);
      url = directionsUrl(origin, chunk[chunk.length - 1].param, chunk.slice(0, -1).map(s => s.param));
      if (url.length <= MAX_URL_LENGTH || size === 1) break;
      size -= 1;
    }
    const chunk = stops.slice(idx, idx + size);
    const last = chunk[chunk.length - 1];
    etapas.push({ desde: originLabel, hasta: last.label, paradas: chunk.map(s => s.label), url });
    origin = last.param;
    originLabel = last.label;
    idx += size;
  }
  return etapas;
};

export const haversineKm = (a, b) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');

// Android: intent que abre la app de Google Maps si está instalada; si no, el navegador abre la URL de respaldo.
export const androidMapsIntent = (url) =>
  `intent://${url.replace(/^https:\/\//, '')}#Intent;scheme=https;package=com.google.android.apps.maps;S.browser_fallback_url=${encodeURIComponent(url)};end`;

/**
 * Click en un enlace de Google Maps. Ordenador: nueva pestaña (target=_blank del enlace).
 * iOS: el enlace universal abre la app de Google Maps si está instalada o una pestaña nueva.
 * Android: se fuerza la app mediante intent (también desde la app instalada de XÉNICOS).
 */
export const handleMapsClick = (e, url) => {
  if (!isAndroid()) return;
  e.preventDefault();
  window.location.href = androidMapsIntent(url);
};
